'use client';

import {
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { InputErrorIcon } from '@/components/form/validation/inputErrorIcon';
import { Link } from '@/components/shared/link';
import { useEnvironment } from '@/contexts/useEnvironment';

import { WelcomePageWrapper } from '../../welcomePageWrapper';

export function DomainInput({
  errorType,
  slotProps: slotPropsProp,
  ...props
}: TextFieldProps & { errorType?: string }) {
  return (
    <div className='relative'>
      <TextField
        error={errorType === 'EON_ACCOUNT_NOT_FOUND'}
        size='medium'
        placeholder='your-company'
        slotProps={{
          ...slotPropsProp,
          htmlInput: {
            ...slotPropsProp?.htmlInput,
            id: 'login-domain',
            style: { height: '36px', paddingRight: 130 },
          },
        }}
        {...props}
      />
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
          gap: '4px',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          right: 12,
        }}
      >
        {errorType === 'EON_ACCOUNT_NOT_FOUND' && (
          <InputErrorIcon errorMessage={errors[errorType]} />
        )}
        <Typography>.console.eon.io</Typography>
      </Stack>
    </div>
  );
}

const errors: Record<string, string> = {
  EON_ACCOUNT_NOT_FOUND: 'Eon account not found for domain',
  PROJECTS_ERROR: 'Failed to get projects',
  JWT_SECRET_MISSING: 'JWT is missing',
  JWT_SIGNING_ERROR: 'Failed to sign jwt',
  AUTH_HEADER_ERROR: 'Failed to set auth header',
};

export function LoginPage() {
  const [domain, setDomain] = useState('');
  const [focused, setFocused] = useState(true);
  const { clientApiBaseUrl, isDev, isTest } = useEnvironment();
  const searchParams = useSearchParams();

  const errorType = searchParams.get('error');

  const onLogin = (domain: string) => {
    if (!domain) {
      return;
    }
    let url = `${clientApiBaseUrl}/login?domain=${domain}`;
    const returnUrl = searchParams.get('returnUrl');

    if (returnUrl) {
      // In dev/test, the API gateway and frontend can run on different
      // origins (e.g. :8080/service DNS vs :3000), so a relative returnUrl
      // would resolve against the gateway origin. In production they share the
      // same origin, so the relative path works as-is.
      const effectiveReturnUrl =
        isDev || isTest ? `${window.location.origin}${returnUrl}` : returnUrl;
      url += `&returnUrl=${encodeURIComponent(effectiveReturnUrl)}`;
    }

    window.location.replace(url);
  };

  useEffect(() => {
    if (isDev && !isTest) {
      const urlDomain = searchParams.get('domain');
      onLogin(urlDomain || 'eon-team');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WelcomePageWrapper>
      <Stack
        sx={{
          gap: '40px',
          overflow: 'hidden',
        }}
      >
        <Typography
          variant='h1'
          sx={{
            maxWidth: '540px',
            wordWrap: 'break-word',
          }}
        >
          Log in with your account’s domain
        </Typography>
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <DomainInput
            autoFocus
            errorType={errorType || undefined}
            focused={focused}
            onBlur={() => setFocused(false)}
            onFocus={() => setFocused(true)}
            slotProps={{ htmlInput: { 'data-testid': 'login-domain' } }}
            value={domain}
            sx={{ width: '320px' }}
            onChange={(e) => {
              const newValue = e.target.value;

              // allow only alphanumeric characters, hyphens, and periods
              if (/^[a-zA-Z0-9-]*$/.test(newValue)) {
                setDomain(newValue);
              }
            }}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                onLogin(domain);
              }
            }}
          />
          <Button
            className='h-full'
            disabled={!domain}
            data-testid='login-button'
            variant='contained'
            onClick={() => onLogin(domain)}
          >
            Login
            <i className='material-symbols-arrow-right-alt ml-[8px] align-middle' />
          </Button>
        </Stack>
        {errorType && errorType !== 'EON_ACCOUNT_NOT_FOUND' && (
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
            }}
          >
            <i className='material-symbols-error-outline-rounded mr-[8px] text-(--mui-palette-error-main)'></i>
            <Typography
              variant='body1'
              data-testid='error-message'
              sx={{ color: 'error.main' }}
            >
              {errors[errorType] || 'Unknown error'}
            </Typography>
          </Stack>
        )}
        <Divider
          sx={{
            borderStyle: 'dashed',
            width: 600,
            borderColor: 'var(--mui-palette-background-lightGray)',
          }}
        />
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {`Don't have an account? `}
          <Link
            className='font-bold'
            href='https://www.eon.io/get-a-demo'
            target='_blank'
          >
            <Button variant='text'>Sign Up</Button>
          </Link>
        </Stack>
      </Stack>
    </WelcomePageWrapper>
  );
}
